import { Geodata } from '@data/Geodata.js';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import {
    MIXED,
    NotificationType,
    SIM_NOISE,
    SIM_TARGET,
    SIM_VVA,
    SIM_WIND,
    type SimulationFile,
    type SimulationSetJSON,
    type SubSimulationJSON,
} from '@visualizer/types';
import { UUID } from 'crypto';
import { FileFetcherV2 } from 'Loader/FileFetcherv2';
import { ApiRoutes } from 'store/ApiRoutes';
import { getArrayBuffer } from 'Loader/IndexedDB_Handler';
import ResultSerializer from 'Loader/serializerV2/ResultSerializer';
import EmptyLayer from 'Simulations/EmptyLayer';
import Layer from '../Simulations/Layer';
import LayerStandard from '../Simulations/LayerStandard';
import { buildEmptyLayerIdForSimulationSet, EMPTY_SIMULATION_NAME } from './emptySimulationIdentity';
import useNotificationStore from '@gui/store/notification';
import { clearLayerFailed, markLayerFailed, removeLayer } from '@gui/store/persistent/persistentStore';

type Categories = {
    solar: Layer[];
    freeview: Layer[];
    targetview: Layer[];
    noise: Layer[];
    wind: Layer[];
    saved: Layer[];
    mixed: Layer[];
};

export type SimulationFileLike = SimulationFile & {
    newFileS3Url?: string;
    s3Key?: string;
    filePath?: string;
    simulation_id?: string;
};

export type LayerDownloadPlan = {
    subSimulation: SubSimulationJSON;
    layerId: string;
    resultsNumber: number;
};

export class SimulationSet {
    id: UUID;

    parentGeodata: Geodata;
    name: string;
    isOpen: boolean = true;

    // Files to be loaded
    files: SimulationFile[] = [];
    simulationSetJSON: SimulationSetJSON;
    // Data
    layerCollection: Layer[] = [];
    hasEmptyLayer: boolean = false;

    layerCollectionByGroup: Categories = {
        solar: [],
        freeview: [],
        targetview: [],
        noise: [],
        wind: [],
        saved: [],
        mixed: [],
    };

    constructor(parentGeodata: Geodata, simulationSetJSON: SimulationSetJSON) {
        this.id = simulationSetJSON?.id as UUID;

        this.parentGeodata = parentGeodata;
        this.name = simulationSetJSON?.name;
        this.simulationSetJSON = simulationSetJSON;
    }

    prepareFiles(simulationFiles: SimulationFile[]) {
        for (let file of simulationFiles) {
            const { url, key, name, layerName, subSimulationId, metadata, fullKey } = file;

            // are this undefined at the beginning?
            const downloaded = file.downloaded;
            const processed = file.processed;
            const remainingBytes = file.remainingBytes;
            const totalSize = file.totalSize;

            this.files.push({
                name,
                url,
                metadata,
                fullKey,
                key,
                downloaded,
                processed,
                remainingBytes,
                totalSize,
                layerName,
                subSimulationId,
                step: 'initializing',
                title: layerName,
            });
        }
    }
    async discoverLayerPlans(fileFetcher: FileFetcherV2): Promise<LayerDownloadPlan[]> {
        const subSimulations = this.simulationSetJSON.simulations.filter((subSimulation) => subSimulation.name !== EMPTY_SIMULATION_NAME);

        const plans = await Promise.all(
            subSimulations.map(async (subSimulation) => {
                const layerId = `${this.id}-${subSimulation.id}`;
                try {
                    // Fetch result layer and mark it as non-failed by calling clear:
                    const resultsNumber = await fileFetcher.fetchResults(this.id, subSimulation.id);
                    clearLayerFailed(layerId);
                    return {
                        subSimulation,
                        layerId,
                        resultsNumber,
                    };
                } catch (error) {
                    // Remove layer and mark it as failed:
                    removeLayer(layerId);
                    const message = error instanceof Error ? error.message : 'Layer loading failed';
                    markLayerFailed(layerId, message);
                    return undefined;
                }
            })
        );

        const validPlans = plans.filter((plan) => plan !== undefined) as LayerDownloadPlan[];

        const plannedDownloads = validPlans.flatMap((plan) =>
            Array.from({ length: plan.resultsNumber }, (_, i) => ({
                key: `${plan.layerId}/results/${i}`,
                endpoint: ApiRoutes.resultDownload(this.id, plan.subSimulation.id, i),
                label: `${plan.subSimulation.name} · result ${i + 1}/${plan.resultsNumber}`,
            }))
        );
        fileFetcher.primeDownloadQueue(plannedDownloads);

        return validPlans;
    }

    async loadAllData(fileFetcher: FileFetcherV2, plans?: LayerDownloadPlan[]) {
        const layerPlans = plans ?? (await this.discoverLayerPlans(fileFetcher));
        const loadingLayers = layerPlans.map(async (plan) => {
            try {
                // Load layer and mark it as non-failed:
                const layer = await this.loadLayerFromPlan(fileFetcher, plan);
                clearLayerFailed(plan.layerId);
                return layer;
            } catch (error) {
                // Failed to load layer, remove it and mark as failed:
                removeLayer(plan.layerId);
                const message = error instanceof Error ? error.message : 'Layer loading failed';
                markLayerFailed(plan.layerId, message);
                return undefined;
            }
        });

        const loadedLayers = (await Promise.all(loadingLayers)).filter((layer) => layer !== undefined) as Layer[];
        if (loadedLayers.length > 0 && this.hasEmptyLayer) {
            const emptyLayerId = buildEmptyLayerIdForSimulationSet(this.id);
            this.layerCollection = this.layerCollection.filter((layer) => layer.getUID() !== emptyLayerId);
            this.hasEmptyLayer = false;
        }
        // Step 2: Add deserialized simulations to layerCollection
        this.layerCollection.push(...loadedLayers);

        this.splitLayersByGroup();
        this.parentGeodata.parentGrid.layersNumber += this.layerCollection.length;

        // Load Empty Layer if necessary:
        if (this.layerCollection.length === 0) {
            this.loadEmptyLayer();
            return;
        }
    }

    private async loadLayerFromPlan(fileFetcher: FileFetcherV2, plan: LayerDownloadPlan): Promise<Layer> {
        const { subSimulation, layerId, resultsNumber } = plan;
        return Promise.resolve()
            .then(async () => {
                const downloadPromises = Array.from({ length: resultsNumber }, (_, i) => i).map((i) =>
                    fileFetcher.downloadFile(`${layerId}/results/${i}`, ApiRoutes.resultDownload(this.id, subSimulation.id, i))
                );

                await Promise.all(downloadPromises);
                return resultsNumber;
            })
            .then((count) => {
                const dataPromises = Array.from({ length: count }, (_, i) => i).map((i) =>
                    getArrayBuffer(`${layerId}/results/${i}`).then((buffer) => {
                        const deserializedResult = new ResultSerializer();
                        return deserializedResult.deserialize(buffer);
                    })
                );

                return Promise.all(dataPromises);
            })
            .then((results) => {
                return new LayerStandard(layerId, this, subSimulation, results);
            })
            .catch((error) => {
                console.error(`Error loading layer ${layerId}:`, error);
                useNotificationStore
                    .getState()
                    .showNotification(`Failed to load layer ${subSimulation.name} - ${error.message}`, NotificationType.Error);
                throw error;
            });
    }

    private async loadLayer(fileFetcher: FileFetcherV2, subSimulation: SubSimulationJSON): Promise<Layer> {
        const layerId = `${this.id}-${subSimulation.id}`;
        const resultsNumber = await fileFetcher.fetchResults(this.id, subSimulation.id);
        fileFetcher.primeDownloadQueue(
            Array.from({ length: resultsNumber }, (_, i) => ({
                key: `${layerId}/results/${i}`,
                endpoint: ApiRoutes.resultDownload(this.id, subSimulation.id, i),
                label: `${subSimulation.name} · result ${i + 1}/${resultsNumber}`,
            }))
        );
        return this.loadLayerFromPlan(fileFetcher, { subSimulation, layerId, resultsNumber });
    }

    async loadSavedLayer(subSimulationJSON: SubSimulationJSON) {
        const fileFetcher = new FileFetcherV2();
        const layer = await this.loadLayer(fileFetcher, subSimulationJSON);

        if (this.hasEmptyLayer) {
            const emptyLayerId = buildEmptyLayerIdForSimulationSet(this.id);
            this.layerCollection = this.layerCollection.filter((existingLayer) => existingLayer.getUID() !== emptyLayerId);
            this.hasEmptyLayer = false;
            this.splitLayersByGroup();
        }

        // Step 3: Add deserialized simulations to layerCollection
        this.layerCollection.push(layer);
        if (layer.isSavedLayer()) this.layerCollectionByGroup.saved.push(layer);
        else if (layer.isOfType(MIXED)) this.layerCollectionByGroup.mixed.push(layer);

        this.parentGeodata.parentGrid.layersNumber += 1;

        const store = usePersistentStore.getState();
        const layerId = layer.getUID();
        if (!store.layers.layerStates[layerId]) {
            store.addLayer(layerId);
        }

        return layer;
    }

    loadEmptyLayer() {
        const emptyLayerId = buildEmptyLayerIdForSimulationSet(this.id);

        if (this.hasEmptyLayer || this.layerCollection.some((layer) => layer.getUID() === emptyLayerId)) {
            return;
        }

        this.hasEmptyLayer = true;
        clearLayerFailed(emptyLayerId);

        // Create Layer:
        const layer = new EmptyLayer(this, emptyLayerId);

        // Create Simulation Set:
        this.layerCollection.push(layer);
        this.splitLayersByGroup();

        // this.name = "Empty Simulation Set";

        // Update grid:
        // grid.geodatas[0].simulationSets.push(this);
        // grid.getLayerCollection().push(layer);
    }

    splitLayersByGroup() {
        this.layerCollectionByGroup = {
            solar: [],
            freeview: [],
            targetview: [],
            noise: [],
            wind: [],
            saved: [],
            mixed: [],
        };

        for (let layer of this.layerCollection) {
            if (layer.isSavedLayer()) this.layerCollectionByGroup.saved.push(layer);
            else if (layer.isSun()) this.layerCollectionByGroup.solar.push(layer);
            else if (layer.isOfType(SIM_VVA)) this.layerCollectionByGroup.freeview.push(layer);
            else if (layer.isOfType(SIM_TARGET)) this.layerCollectionByGroup.targetview.push(layer);
            else if (layer.isOfType(SIM_NOISE)) this.layerCollectionByGroup.noise.push(layer);
            else if (layer.isOfType(SIM_WIND)) this.layerCollectionByGroup.wind.push(layer);
            else if (layer.isOfType(MIXED)) this.layerCollectionByGroup.mixed.push(layer);
            else this.layerCollectionByGroup.solar.push(layer);
        }

        // Sort solar group properly:
        this.layerCollectionByGroup.solar.sort((a, b) => {
            const order: Record<string, number> = { Yearly: 0, Monthly: 1, Hourly: 2 };

            const getOrderValue = (name?: string) => {
                if (!name) return 999; // place unknown names at the end
                const key = Object.keys(order).find((k) => name.includes(k));
                return key ? order[key] : 999;
            };

            return getOrderValue(a.name) - getOrderValue(b.name);
        });

        // Flatten Collection by Group in Collection to preserve correct order:
        this.layerCollection = [
            ...this.layerCollectionByGroup.solar,
            ...this.layerCollectionByGroup.freeview,
            ...this.layerCollectionByGroup.targetview,
            ...this.layerCollectionByGroup.noise,
            ...this.layerCollectionByGroup.wind,
            ...this.layerCollectionByGroup.saved,
            ...this.layerCollectionByGroup.mixed,
        ];
    }
}
